import { Link, useParams } from "@tanstack/react-router";
import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle,
  ChevronRight,
  DollarSign,
  Download,
  FileText,
  Percent,
  Plus,
  RefreshCw,
  Save,
  Search,
  Send,
  Trash2,
  XCircle,
} from "lucide-react";
import { Fragment, useMemo, useState } from "react";
import { toast } from "sonner";
import { GcDirectDrillDownSheet } from "@/components/pay-apps/GcDirectDrillDownSheet";
import { SubSourcedDrillDownSheet } from "@/components/pay-apps/SubSourcedDrillDownSheet";
import { AttachmentSection } from "@/components/shared/AttachmentSection";
import { ErrorState } from "@/components/shared/error-state";
import { ReauthDialog } from "@/components/shared/ReauthDialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { useCostCodes } from "@/hooks/use-cost-codes";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import { useApprovedGrBlockers } from "@/hooks/use-gr-invoices";
import type {
  GcDirectLineItemInput,
  GrRefreshDiffItem,
  OwnerPayAppLineItem,
  RefreshDiffItem,
} from "@/hooks/use-pay-applications";
import {
  useCertifyOwnerPayApp,
  useCreateGcDirectLineItem,
  useDeleteGcDirectLineItem,
  useGenerateOwnerPayAppPdf,
  useOwnerPayApp,
  useOwnerPayAppGrRefreshPreview,
  useOwnerPayAppRefreshPreview,
  useOwnerPayAppSubBreakdown,
  useRefreshOwnerPayAppFromGRs,
  useRefreshOwnerPayAppFromSubs,
  useRejectOwnerPayApp,
  useSubmitOwnerPayApp,
  useUpdateOwnerPayApp,
  useUpdateOwnerPayAppLineItems,
} from "@/hooks/use-pay-applications";
import { useReauth } from "@/hooks/use-reauth";
import { useSession } from "@/lib/auth-client";
import { downloadCsv } from "@/lib/csv";
import { cn } from "@/lib/utils";

// ── Helpers ──

function formatCurrency(value: number | string | null | undefined): string {
  if (value === null || value === undefined || value === "") return "--";
  const num = typeof value === "number" ? value : Number(value);
  if (Number.isNaN(num)) return "--";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(num);
}

function populatedAmt(v: number): string {
  return v !== 0 ? "text-blue-600 dark:text-blue-400 font-medium" : "";
}

function pcoAmt(v: number): string {
  return v !== 0 ? "text-amber-600 dark:text-amber-400 font-medium" : "";
}

function formatDate(date: string | null | undefined): string {
  if (!date) return "--";
  return new Date(date).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function statusVariant(
  status: string | null,
): "success" | "warning" | "secondary" | "destructive" | "default" {
  const s = status?.toUpperCase();
  switch (s) {
    case "APPROVED":
    case "CERTIFIED":
    case "PAID":
      return "success";
    case "DRAFT":
    case "SUBMITTED":
      return "warning";
    case "REJECTED":
      return "destructive";
    default:
      return "default";
  }
}

function formatStatusLabel(status: string | null): string {
  if (!status) return "--";
  return status
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
}

// ── Division Grouping ──

interface DivisionGroup {
  division: string;
  label: string;
  items: OwnerPayAppLineItem[];
}

function getDivisionLabel(division: string): string {
  if (division === "50") return "Division 50 — GC Direct Costs";
  if (division === "00") return "Uncategorized";
  return `Division ${division}`;
}

function groupLineItemsByDivision(items: OwnerPayAppLineItem[]): DivisionGroup[] {
  const map = new Map<string, OwnerPayAppLineItem[]>();
  for (const item of items) {
    const key = item.lineType === "gc_direct" ? "50" : (item.csiDivision ?? "00");
    const existing = map.get(key);
    if (existing) {
      existing.push(item);
    } else {
      map.set(key, [item]);
    }
  }
  return Array.from(map.entries())
    .sort(([a], [b]) => {
      if (a === "50") return 1;
      if (b === "50") return -1;
      return a.localeCompare(b);
    })
    .map(([division, groupItems]) => ({
      division,
      label: getDivisionLabel(division),
      items: groupItems,
    }));
}

// ── Sub-total helpers ──

interface LineTotals {
  scheduledValue: number;
  revisedScheduledValue: number;
  prevApplications: number;
  completedThisPeriod: number;
  storedMaterials: number;
  totalCompletedStored: number;
  retainage: number;
}

const ZERO_TOTALS: LineTotals = {
  scheduledValue: 0,
  revisedScheduledValue: 0,
  prevApplications: 0,
  completedThisPeriod: 0,
  storedMaterials: 0,
  totalCompletedStored: 0,
  retainage: 0,
};

// ── Add GC Direct form defaults ──

const defaultAddForm: GcDirectLineItemInput = {
  description: "",
  scheduledValue: "",
  adjustmentToSchedule: "",
  approvedChangeOrders: "",
  completedThisPeriod: "",
  storedMaterials: "",
  costCodeId: "",
  retentionPercent: "",
};

// ── Constants ──

const COL_COUNT = 15;

// ── Component ──

export function OwnerPayAppDetailPage() {
  const { id } = useParams({ strict: false });
  const payAppId = id ?? "";

  const { data: payApp, isLoading, isError, error, refetch } = useOwnerPayApp(payAppId);
  const { data: session } = useSession();
  const { data: costCodes } = useCostCodes();

  // GR: approved-but-not-posted invoices (submit blocker). enabled only when payApp loaded.
  const { data: grBlockers } = useApprovedGrBlockers(
    payApp?.projectId ?? "",
    payApp?.billingPeriodId ?? null,
  );
  const {
    dialogOpen: reauthOpen,
    onDialogOpenChange: onReauthOpenChange,
    withReauth,
    onVerified,
  } = useReauth();

  // Mutations / queries
  const submitMutation = useSubmitOwnerPayApp();
  const certifyMutation = useCertifyOwnerPayApp();
  const rejectMutation = useRejectOwnerPayApp();
  const refreshFromSubsMutation = useRefreshOwnerPayAppFromSubs(payAppId);
  const refreshFromGRsMutation = useRefreshOwnerPayAppFromGRs(payAppId);
  const updateLineItemsMutation = useUpdateOwnerPayAppLineItems(payAppId);
  const updateOwnerPayAppMutation = useUpdateOwnerPayApp(payAppId);
  const createGcDirectMutation = useCreateGcDirectLineItem(payAppId);
  const deleteGcDirectMutation = useDeleteGcDirectLineItem(payAppId);
  const generatePdfMutation = useGenerateOwnerPayAppPdf(payAppId);
  const { data: subBreakdown } = useOwnerPayAppSubBreakdown(payAppId);

  // Map: ownerLineId → total pending sub amount (unapproved contributions)
  const pendingByLineId = useMemo(() => {
    const map = new Map<string, number>();
    for (const entry of subBreakdown ?? []) {
      const pending = entry.contributions
        .filter((c) => c.approvalStatus !== "APPROVED" && c.approvalStatus !== "approved")
        .reduce((sum, c) => sum + c.totalAmount, 0);
      if (pending > 0) map.set(entry.ownerLineId, pending);
    }
    return map;
  }, [subBreakdown]);

  // UI state
  const [editedItems, setEditedItems] = useState<
    Record<string, { completedThisPeriod: string; storedMaterials: string }>
  >({});
  const [gcPctMode, setGcPctMode] = useState<Record<string, boolean>>({});
  const [gcPctValues, setGcPctValues] = useState<Record<string, string>>({});

  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");

  const [refreshPreviewOpen, setRefreshPreviewOpen] = useState(false);
  const { data: refreshPreviewData, isLoading: previewLoading } = useOwnerPayAppRefreshPreview(
    payAppId,
    refreshPreviewOpen,
  );

  const [grRefreshPreviewOpen, setGrRefreshPreviewOpen] = useState(false);
  const { data: grRefreshPreviewData, isLoading: grPreviewLoading } =
    useOwnerPayAppGrRefreshPreview(payAppId, grRefreshPreviewOpen);

  const [addGcDirectOpen, setAddGcDirectOpen] = useState(false);
  const [addGcForm, setAddGcForm] = useState<GcDirectLineItemInput>(defaultAddForm);

  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  const [subDrillDown, setSubDrillDown] = useState<{
    costCodeId: string | null;
    description: string;
    thisPeriodTotal: number;
  } | null>(null);
  const [gcDrillDown, setGcDrillDown] = useState<{
    lineItemId: string;
    lineDescription: string;
  } | null>(null);

  const [g703Search, setG703Search] = useState("");
  const debouncedG703Search = useDebouncedValue(g703Search, 200);

  const filteredLineItems = useMemo(() => {
    const items = payApp?.lineItems ?? [];
    const q = debouncedG703Search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((item) => {
      const divLabel = getDivisionLabel(
        item.lineType === "gc_direct" ? "50" : (item.csiDivision ?? "00"),
      ).toLowerCase();
      const amtFields = [
        item.scheduledValue,
        item.revisedScheduledValue,
        item.completedThisPeriod,
        item.storedMaterials,
        item.prevApplications,
        item.totalCompletedStored,
        item.retainage,
      ].map((v) => String(Math.round(v)));
      return [
        item.description ?? "",
        item.costCodeCode ?? "",
        item.csiDivision ?? "",
        divLabel,
        item.itemNo ?? "",
        ...amtFields,
      ]
        .join(" ")
        .toLowerCase()
        .includes(q);
    });
  }, [payApp, debouncedG703Search]);

  // ── Loading/error states ──

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (isError) {
    return (
      <ErrorState
        message={error?.message ?? "Failed to load pay application"}
        onRetry={() => refetch()}
      />
    );
  }

  if (!payApp) {
    return <ErrorState message="Owner pay application not found" />;
  }

  // ── Derived values ──

  const isDraft = payApp.status === "DRAFT";
  const isSubmitted = payApp.status === "SUBMITTED";
  const isPmOrAdmin = session?.user?.role === "admin" || session?.user?.role === "project_manager";
  const isAdmin = session?.user?.role === "admin";
  const lineItems = payApp.lineItems ?? [];
  const retPct = payApp.retainagePct;

  const divisionGroups = groupLineItemsByDivision(filteredLineItems);

  const exportBaseName = (() => {
    const projectSlug = payApp.projectNumber?.trim()
      ? payApp.projectNumber.trim()
      : (payApp.projectName || "Untitled").replace(/[^a-zA-Z0-9]+/g, "");
    const periodDate = payApp.periodEnd ?? "undated";
    return `${projectSlug}_${periodDate}`;
  })();

  const handleExportG702 = () => {
    const rows = [
      { line: "1", description: "Original Contract Sum", amount: payApp.originalContractSum },
      {
        line: "2",
        description: "Net Change by Change Orders",
        amount: payApp.approvedChangeOrders,
      },
      { line: "3", description: "Contract Sum to Date (1 + 2)", amount: payApp.contractSumToDate },
      {
        line: "4",
        description: "Total Completed & Stored to Date",
        amount: payApp.totalCompletedStored,
      },
      { line: "5", description: `Retainage (${retPct}%)`, amount: payApp.retainageAmount },
      {
        line: "6",
        description: "Total Earned Less Retainage (4 − 5)",
        amount: payApp.totalEarnedLessRetainage,
      },
      {
        line: "7",
        description: "Less Previous Certificates",
        amount: payApp.lessPreviousCertificates,
      },
      { line: "8", description: "Current Payment Due (6 − 7)", amount: payApp.currentPaymentDue },
      {
        line: "9",
        description: "Balance to Finish Including Retainage",
        amount: payApp.balanceToFinishIncRetainage,
      },
    ];
    downloadCsv(`${exportBaseName}-G702_GCtoOwner_PayApp.csv`, rows, [
      { key: "line", label: "Line" },
      { key: "description", label: "Description" },
      { key: "amount", label: "Amount" },
    ]);
  };

  const handleExportG703 = () => {
    const rows = filteredLineItems.map((item) => {
      const division = item.lineType === "gc_direct" ? "50" : (item.csiDivision ?? "00");
      const total = item.prevApplications + item.completedThisPeriod + item.storedMaterials;
      const revised =
        item.revisedScheduledValue > 0 ? item.revisedScheduledValue : item.scheduledValue;
      const pct = revised > 0 ? (total / revised) * 100 : 0;
      const balance = revised - total;
      return {
        itemNo: item.itemNo ?? "",
        division,
        divisionLabel: getDivisionLabel(division),
        costCode: item.costCodeCode ?? "",
        description: item.description ?? "",
        scheduledValue: item.scheduledValue,
        adjustmentToSchedule: item.adjustmentToSchedule,
        approvedChangeOrders: item.approvedChangeOrders,
        revisedScheduledValue: item.revisedScheduledValue,
        prevApplications: item.prevApplications,
        completedThisPeriod: item.completedThisPeriod,
        storedMaterials: item.storedMaterials,
        totalCompletedStored: total,
        percentComplete: pct.toFixed(2),
        balanceToFinish: balance,
        retainage: item.retainage,
        source: item.lineType === "gc_direct" ? "GC Direct" : "Sub Sourced",
      };
    });
    downloadCsv(`${exportBaseName}-G703_GCtoOwner_PayApp.csv`, rows, [
      { key: "itemNo", label: "A — Item #" },
      { key: "division", label: "CSI Division" },
      { key: "divisionLabel", label: "Division Label" },
      { key: "costCode", label: "Cost Code" },
      { key: "description", label: "B — Description" },
      { key: "scheduledValue", label: "C — Scheduled Value" },
      { key: "adjustmentToSchedule", label: "D — Adjustments" },
      { key: "approvedChangeOrders", label: "E — Approved COs" },
      { key: "revisedScheduledValue", label: "F — Revised" },
      { key: "prevApplications", label: "G — Previous" },
      { key: "completedThisPeriod", label: "H — This Period" },
      { key: "storedMaterials", label: "I — Stored" },
      { key: "totalCompletedStored", label: "J — Total" },
      { key: "percentComplete", label: "K — %" },
      { key: "balanceToFinish", label: "L — Balance" },
      { key: "retainage", label: "Retainage" },
      { key: "source", label: "Source" },
    ]);
  };

  // ── Edit helpers ──

  function getEditedValue(
    itemId: string,
    field: "completedThisPeriod" | "storedMaterials",
  ): string | undefined {
    return editedItems[itemId]?.[field];
  }

  function setEditedValue(
    itemId: string,
    field: "completedThisPeriod" | "storedMaterials",
    value: string,
  ) {
    setEditedItems((prev) => ({
      ...prev,
      [itemId]: {
        completedThisPeriod: prev[itemId]?.completedThisPeriod ?? "",
        storedMaterials: prev[itemId]?.storedMaterials ?? "",
        [field]: value,
      },
    }));
  }

  function computeEditedLine(item: OwnerPayAppLineItem) {
    const editedCompleted = getEditedValue(item.id, "completedThisPeriod");
    const editedStored = getEditedValue(item.id, "storedMaterials");
    const completedThisPeriod =
      editedCompleted !== undefined ? Number(editedCompleted) || 0 : item.completedThisPeriod;
    const storedMaterials =
      editedStored !== undefined ? Number(editedStored) || 0 : item.storedMaterials;
    const totalCompletedStored = item.prevApplications + completedThisPeriod + storedMaterials;
    const revised =
      item.revisedScheduledValue > 0 ? item.revisedScheduledValue : item.scheduledValue;
    const pct = revised > 0 ? (totalCompletedStored / revised) * 100 : 0;
    const balanceToFinish = revised - totalCompletedStored;
    const effectiveRetPct = item.retentionPercent !== null ? Number(item.retentionPercent) : retPct;
    const retainage = totalCompletedStored * (effectiveRetPct / 100);
    return {
      completedThisPeriod,
      storedMaterials,
      totalCompletedStored,
      pct,
      balanceToFinish,
      retainage,
    };
  }

  function computeDivisionTotals(items: OwnerPayAppLineItem[]): LineTotals {
    return items.reduce<LineTotals>(
      (acc, item) => {
        const computed = computeEditedLine(item);
        return {
          scheduledValue: acc.scheduledValue + item.scheduledValue,
          revisedScheduledValue: acc.revisedScheduledValue + item.revisedScheduledValue,
          prevApplications: acc.prevApplications + item.prevApplications,
          completedThisPeriod: acc.completedThisPeriod + computed.completedThisPeriod,
          storedMaterials: acc.storedMaterials + computed.storedMaterials,
          totalCompletedStored: acc.totalCompletedStored + computed.totalCompletedStored,
          retainage: acc.retainage + computed.retainage,
        };
      },
      { ...ZERO_TOTALS },
    );
  }

  function computeGrandTotals(): LineTotals {
    return lineItems.reduce<LineTotals>(
      (acc, item) => {
        const computed = computeEditedLine(item);
        return {
          scheduledValue: acc.scheduledValue + item.scheduledValue,
          revisedScheduledValue: acc.revisedScheduledValue + item.revisedScheduledValue,
          prevApplications: acc.prevApplications + item.prevApplications,
          completedThisPeriod: acc.completedThisPeriod + computed.completedThisPeriod,
          storedMaterials: acc.storedMaterials + computed.storedMaterials,
          totalCompletedStored: acc.totalCompletedStored + computed.totalCompletedStored,
          retainage: acc.retainage + computed.retainage,
        };
      },
      { ...ZERO_TOTALS },
    );
  }

  // ── % mode toggle ──

  function handlePctInput(item: OwnerPayAppLineItem, pctStr: string) {
    setGcPctValues((prev) => ({ ...prev, [item.id]: pctStr }));
    const pct = Number(pctStr) || 0;
    const revised =
      item.revisedScheduledValue > 0 ? item.revisedScheduledValue : item.scheduledValue;
    const newCompleted = Math.max(0, (pct / 100) * revised - item.prevApplications);
    setEditedValue(item.id, "completedThisPeriod", String(newCompleted.toFixed(2)));
  }

  function togglePctMode(itemId: string) {
    setGcPctMode((prev) => ({ ...prev, [itemId]: !prev[itemId] }));
    setGcPctValues((prev) => ({ ...prev, [itemId]: "" }));
  }

  // ── Action handlers ──

  function handleSave() {
    const updates = Object.entries(editedItems).map(([itemId, values]) => ({
      id: itemId,
      completedThisPeriod: values.completedThisPeriod || "0",
      storedMaterials: values.storedMaterials || "0",
    }));
    if (updates.length === 0) {
      toast.error("No changes to save");
      return;
    }
    updateLineItemsMutation.mutate({ lineItems: updates }, { onSuccess: () => setEditedItems({}) });
  }

  function handleReject() {
    if (!rejectReason.trim()) {
      toast.error("Please provide a rejection reason");
      return;
    }
    rejectMutation.mutate(
      { id: payAppId, reason: rejectReason },
      {
        onSuccess: () => {
          setRejectDialogOpen(false);
          setRejectReason("");
        },
      },
    );
  }

  function handleRefreshApply() {
    refreshFromSubsMutation.mutate(undefined, {
      onSuccess: () => setRefreshPreviewOpen(false),
    });
  }

  function handleAddGcDirect() {
    if (!addGcForm.description.trim() || !addGcForm.scheduledValue) {
      toast.error("Description and Scheduled Value are required");
      return;
    }
    const input: GcDirectLineItemInput = {
      description: addGcForm.description,
      scheduledValue: addGcForm.scheduledValue,
      ...(addGcForm.adjustmentToSchedule
        ? { adjustmentToSchedule: addGcForm.adjustmentToSchedule }
        : {}),
      ...(addGcForm.approvedChangeOrders
        ? { approvedChangeOrders: addGcForm.approvedChangeOrders }
        : {}),
      ...(addGcForm.completedThisPeriod
        ? { completedThisPeriod: addGcForm.completedThisPeriod }
        : {}),
      ...(addGcForm.storedMaterials ? { storedMaterials: addGcForm.storedMaterials } : {}),
      ...(addGcForm.costCodeId ? { costCodeId: addGcForm.costCodeId } : {}),
      ...(addGcForm.retentionPercent ? { retentionPercent: addGcForm.retentionPercent } : {}),
    };
    createGcDirectMutation.mutate(input, {
      onSuccess: () => {
        setAddGcDirectOpen(false);
        setAddGcForm(defaultAddForm);
      },
    });
  }

  function handleDeleteGcDirect(lineItemId: string) {
    deleteGcDirectMutation.mutate(lineItemId, {
      onSuccess: () => setDeleteConfirmId(null),
    });
  }

  const hasEdits = Object.keys(editedItems).length > 0;
  const grandTotals = computeGrandTotals();
  const previewItems: RefreshDiffItem[] = (refreshPreviewData?.preview ?? []).filter(
    (d) =>
      d.current.completedThisPeriod !== 0 ||
      d.proposed.completedThisPeriod !== 0 ||
      d.current.approvedChangeOrders !== d.proposed.approvedChangeOrders,
  );

  // ── Render ──

  const hasGrBlockers = (grBlockers ?? []).length > 0;

  return (
    <div className="space-y-6">
      {/* GR submit blocker banner */}
      {isDraft && hasGrBlockers ? (
        <div className="flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3">
          <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-600" />
          <div className="text-sm">
            <p className="font-semibold text-amber-800">
              {grBlockers!.length} GR invoice
              {grBlockers!.length !== 1 ? "s" : ""} approved but not yet posted to Vista
            </p>
            <p className="mt-0.5 text-amber-700">
              These invoices must be posted to Vista before this pay application can be submitted.
              Post them on the{" "}
              <a href="/gr/pending-vista" className="underline hover:no-underline">
                Pending Vista
              </a>{" "}
              page.
            </p>
            <ul className="mt-2 space-y-0.5 text-xs text-amber-700">
              {grBlockers!.map((inv) => (
                <li key={inv.id}>
                  {inv.vendorNameRaw} — #{inv.invoiceNumber} ($
                  {Number(inv.invoiceAmount).toLocaleString("en-US", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                  )
                </li>
              ))}
            </ul>
          </div>
        </div>
      ) : null}

      {/* Header */}
      <div className="flex items-center gap-3">
        <Link to="/pay-applications/owner">
          <Button variant="ghost" size="icon">
            <ArrowLeft className="h-4 w-4" />
          </Button>
        </Link>
        <div className="flex-1">
          <h1 className="text-2xl font-bold">
            Owner Pay Application #{payApp.applicationNo ?? "--"}
          </h1>
          <div className="mt-1 flex items-center gap-2">
            <Badge variant={statusVariant(payApp.status)}>{formatStatusLabel(payApp.status)}</Badge>
            <span className="text-sm text-muted-foreground">
              Period to {formatDate(payApp.periodEnd)}
            </span>
            {payApp.projectName ? (
              <span className="text-sm text-muted-foreground">| {payApp.projectName}</span>
            ) : null}
            {payApp.amountCertified ? (
              <span className="text-sm font-medium text-green-600">
                Certified: {formatCurrency(payApp.amountCertified)}
              </span>
            ) : null}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isPmOrAdmin ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() => generatePdfMutation.mutate()}
              disabled={generatePdfMutation.isPending}
            >
              <FileText className="mr-2 h-4 w-4" />
              {generatePdfMutation.isPending ? "Generating…" : "Generate PDF"}
            </Button>
          ) : null}
          {(() => {
            const pdfAttachment = payApp.attachments.find((a) => a.payAppType === "pdf");
            if (!pdfAttachment) return null;
            const projectSlug = payApp.projectNumber?.trim()
              ? payApp.projectNumber.trim()
              : (payApp.projectName || "Untitled").replace(/[^a-zA-Z0-9]+/g, "");
            const periodDate = payApp.periodEnd ?? "undated";
            const downloadName = `${projectSlug}_${periodDate}-G702_G703_GCtoOwner_PayApp.pdf`;
            return (
              <Button variant="outline" size="sm" asChild>
                <a
                  href={`/api/pay-applications/owner/${payAppId}/attachments/${pdfAttachment.id}`}
                  download={downloadName}
                >
                  <Download className="mr-2 h-4 w-4" />
                  Download PDF
                </a>
              </Button>
            );
          })()}
          <Button variant="outline" size="sm" asChild>
            <a
              href={`/api/pay-applications/owner/${payAppId}/gr-backup-pdf`}
              download={`GR_Backup_${payApp.periodEnd ?? "undated"}.pdf`}
            >
              <Download className="mr-2 h-4 w-4" />
              GR Backup PDF
            </a>
          </Button>
          {isDraft && isPmOrAdmin ? (
            <Button
              variant="outline"
              onClick={() => setRefreshPreviewOpen(true)}
              disabled={refreshFromSubsMutation.isPending}
            >
              <RefreshCw className="mr-2 h-4 w-4" />
              Refresh from Subs
            </Button>
          ) : null}
          {isDraft && isPmOrAdmin ? (
            <Button
              variant="outline"
              onClick={() => setGrRefreshPreviewOpen(true)}
              disabled={refreshFromGRsMutation.isPending}
            >
              <RefreshCw className="mr-2 h-4 w-4" />
              Refresh from GRs
            </Button>
          ) : null}
          {isDraft && hasEdits ? (
            <Button onClick={handleSave} disabled={updateLineItemsMutation.isPending}>
              <Save className="mr-2 h-4 w-4" />
              {updateLineItemsMutation.isPending ? "Saving…" : "Save Changes"}
            </Button>
          ) : null}
          {isDraft ? (
            <Button
              onClick={() => submitMutation.mutate(payAppId)}
              disabled={submitMutation.isPending || hasEdits}
            >
              <Send className="mr-2 h-4 w-4" />
              Submit
            </Button>
          ) : null}
          {isSubmitted && isAdmin ? (
            <>
              <Button
                onClick={() =>
                  withReauth((token) =>
                    certifyMutation.mutate({ id: payAppId, reauthToken: token }),
                  )
                }
                disabled={certifyMutation.isPending}
              >
                <CheckCircle className="mr-2 h-4 w-4" />
                Certify
              </Button>
              <Button
                variant="destructive"
                onClick={() => setRejectDialogOpen(true)}
                disabled={rejectMutation.isPending}
              >
                <XCircle className="mr-2 h-4 w-4" />
                Reject
              </Button>
            </>
          ) : null}
        </div>
      </div>

      {/* G702 / G703 Tabs */}
      <Tabs defaultValue="g702">
        <TabsList>
          <TabsTrigger value="g702">G702 Summary</TabsTrigger>
          <TabsTrigger value="g703">G703 Continuation</TabsTrigger>
        </TabsList>

        <TabsContent value="g702">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between gap-4">
                <CardTitle className="text-base">
                  G702 — Application and Certificate for Payment
                </CardTitle>
                <Button variant="outline" size="sm" onClick={handleExportG702}>
                  <Download className="mr-2 h-4 w-4" />
                  Export CSV
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between border-b py-1.5">
                  <span>1. Original Contract Sum</span>
                  <span className="font-medium">{formatCurrency(payApp.originalContractSum)}</span>
                </div>
                <div className="flex justify-between border-b py-1.5">
                  <span>2. Net Change by Change Orders</span>
                  <span className="font-medium">{formatCurrency(payApp.approvedChangeOrders)}</span>
                </div>
                <div className="flex justify-between border-b py-1.5">
                  <span>3. Contract Sum to Date (1 + 2)</span>
                  <span className="font-medium">{formatCurrency(payApp.contractSumToDate)}</span>
                </div>
                <div className="flex justify-between border-b py-1.5">
                  <span>4. Total Completed &amp; Stored to Date</span>
                  <span className="font-medium">{formatCurrency(payApp.totalCompletedStored)}</span>
                </div>
                <div className="flex justify-between border-b py-1.5">
                  <span>5. Retainage ({retPct}%)</span>
                  <span className="font-medium">{formatCurrency(payApp.retainageAmount)}</span>
                </div>
                <div className="flex justify-between border-b py-1.5">
                  <span>6. Total Earned Less Retainage (4 − 5)</span>
                  <span className="font-medium">
                    {formatCurrency(payApp.totalEarnedLessRetainage)}
                  </span>
                </div>
                <div className="flex justify-between border-b py-1.5">
                  <span>7. Less Previous Certificates</span>
                  <span className="font-medium">
                    {formatCurrency(payApp.lessPreviousCertificates)}
                  </span>
                </div>
                <div className="flex justify-between border-b-2 border-foreground py-2 text-base font-bold">
                  <span>8. Current Payment Due (6 − 7)</span>
                  <span>{formatCurrency(payApp.currentPaymentDue)}</span>
                </div>
                <div className="flex justify-between py-1.5">
                  <span>9. Balance to Finish Including Retainage</span>
                  <span className="font-medium">
                    {formatCurrency(payApp.balanceToFinishIncRetainage)}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="g703">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between gap-4">
                <CardTitle className="text-base">G703 — Continuation Sheet</CardTitle>
                <div className="flex items-center gap-2">
                  <div className="relative w-64">
                    <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                    <input
                      type="text"
                      placeholder="Search division, description, cost code, amount…"
                      value={g703Search}
                      onChange={(e) => setG703Search(e.target.value)}
                      className="h-8 w-full rounded-md border border-input bg-background pl-8 pr-3 text-xs placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
                    />
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleExportG703}
                    disabled={filteredLineItems.length === 0}
                  >
                    <Download className="mr-2 h-4 w-4" />
                    Export CSV
                  </Button>
                </div>
              </div>
            </CardHeader>
            {lineItems.length === 0 ? (
              <CardContent className="py-8 text-center text-muted-foreground">
                No line items. Use &ldquo;Refresh from Subs&rdquo; to populate from approved sub pay
                apps, or add GC Direct cost rows.
              </CardContent>
            ) : filteredLineItems.length === 0 ? (
              <CardContent className="py-8 text-center text-muted-foreground text-sm">
                No line items match &ldquo;{debouncedG703Search}&rdquo;.
              </CardContent>
            ) : (
              <div className="overflow-x-auto">
                <Table className="min-w-[1400px] text-xs">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10">A</TableHead>
                      <TableHead className="min-w-[180px]">B — Description</TableHead>
                      <TableHead className="text-right">C — Sched. Val.</TableHead>
                      <TableHead className="text-right">D — Adjust.</TableHead>
                      <TableHead className="text-right">E — App&apos;d COs</TableHead>
                      <TableHead className="text-right">F — Revised</TableHead>
                      <TableHead className="text-right">G — Previous</TableHead>
                      <TableHead className="text-right">H — This Period</TableHead>
                      <TableHead className="text-right">I — Stored</TableHead>
                      <TableHead className="text-right">J — Total</TableHead>
                      <TableHead className="text-right w-16">K — %</TableHead>
                      <TableHead className="text-right">L — Balance</TableHead>
                      <TableHead className="text-right">Retainage</TableHead>
                      <TableHead className="w-20">Source</TableHead>
                      <TableHead className="w-8" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {divisionGroups.map((group) => {
                      const divTotals = computeDivisionTotals(group.items);
                      return (
                        <Fragment key={`div-${group.division}`}>
                          {/* Division header */}
                          <TableRow
                            key={`div-hdr-${group.division}`}
                            className="bg-muted/60 hover:bg-muted/60"
                          >
                            <TableCell colSpan={COL_COUNT} className="py-1.5 font-bold text-xs">
                              {group.label}
                            </TableCell>
                          </TableRow>

                          {/* Line item rows */}
                          {group.items.map((item, idx) => {
                            const computed = computeEditedLine(item);
                            const isGcDirect = item.lineType === "gc_direct";
                            const editableInDraft = isDraft && isGcDirect && isPmOrAdmin;
                            const inPctMode = !!gcPctMode[item.id];
                            const canDelete =
                              isGcDirect && isDraft && isPmOrAdmin && item.prevApplications === 0;

                            return (
                              <TableRow key={item.id} className="hover:bg-muted/20">
                                <TableCell className="font-mono text-muted-foreground">
                                  {item.itemNo ?? String(idx + 1)}
                                </TableCell>
                                <TableCell className="max-w-[220px]">
                                  <div>
                                    <span className="block truncate">
                                      {item.description ?? "--"}
                                    </span>
                                    {item.costCodeCode ? (
                                      <span className="text-[10px] text-muted-foreground">
                                        {item.costCodeCode}
                                      </span>
                                    ) : null}
                                  </div>
                                </TableCell>
                                <TableCell className="text-right">
                                  {formatCurrency(item.scheduledValue)}
                                </TableCell>
                                <TableCell className="text-right">
                                  {item.adjustmentToSchedule !== 0
                                    ? formatCurrency(item.adjustmentToSchedule)
                                    : "--"}
                                </TableCell>
                                <TableCell
                                  className={cn("text-right", pcoAmt(item.approvedChangeOrders))}
                                >
                                  {item.approvedChangeOrders !== 0
                                    ? formatCurrency(item.approvedChangeOrders)
                                    : "--"}
                                </TableCell>
                                <TableCell className="text-right font-medium">
                                  {formatCurrency(item.revisedScheduledValue)}
                                </TableCell>
                                <TableCell
                                  className={cn("text-right", populatedAmt(item.prevApplications))}
                                >
                                  {formatCurrency(item.prevApplications)}
                                </TableCell>
                                {/* H — This Period */}
                                <TableCell className="text-right">
                                  {editableInDraft ? (
                                    <div className="flex items-center justify-end gap-1">
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-5 w-5 shrink-0"
                                        title={
                                          inPctMode ? "Switch to dollar input" : "Switch to % input"
                                        }
                                        onClick={() => togglePctMode(item.id)}
                                      >
                                        {inPctMode ? (
                                          <DollarSign className="h-3 w-3" />
                                        ) : (
                                          <Percent className="h-3 w-3" />
                                        )}
                                      </Button>
                                      {inPctMode ? (
                                        <Input
                                          type="number"
                                          step="0.1"
                                          min="0"
                                          max="100"
                                          placeholder="%"
                                          className="w-20 text-right h-7 text-xs"
                                          value={gcPctValues[item.id] ?? ""}
                                          onChange={(e) => handlePctInput(item, e.target.value)}
                                        />
                                      ) : (
                                        <Input
                                          type="number"
                                          step="0.01"
                                          min="0"
                                          className="w-24 text-right h-7 text-xs"
                                          value={
                                            getEditedValue(item.id, "completedThisPeriod") ??
                                            String(item.completedThisPeriod)
                                          }
                                          onChange={(e) =>
                                            setEditedValue(
                                              item.id,
                                              "completedThisPeriod",
                                              e.target.value,
                                            )
                                          }
                                        />
                                      )}
                                    </div>
                                  ) : (
                                    (() => {
                                      const pendingAmt = pendingByLineId.get(item.id) ?? 0;
                                      if (computed.completedThisPeriod === 0 && pendingAmt > 0) {
                                        return (
                                          <span
                                            className="text-amber-600 dark:text-amber-400 font-medium"
                                            title="Pending sub pay app — not yet approved"
                                          >
                                            {formatCurrency(pendingAmt)}
                                          </span>
                                        );
                                      }
                                      return (
                                        <span
                                          className={populatedAmt(computed.completedThisPeriod)}
                                        >
                                          {formatCurrency(computed.completedThisPeriod)}
                                        </span>
                                      );
                                    })()
                                  )}
                                </TableCell>
                                {/* I — Stored */}
                                <TableCell className="text-right">
                                  {editableInDraft ? (
                                    <Input
                                      type="number"
                                      step="0.01"
                                      min="0"
                                      className="ml-auto w-24 text-right h-7 text-xs"
                                      value={
                                        getEditedValue(item.id, "storedMaterials") ??
                                        String(item.storedMaterials)
                                      }
                                      onChange={(e) =>
                                        setEditedValue(item.id, "storedMaterials", e.target.value)
                                      }
                                    />
                                  ) : (
                                    <span className={populatedAmt(computed.storedMaterials)}>
                                      {formatCurrency(computed.storedMaterials)}
                                    </span>
                                  )}
                                </TableCell>
                                <TableCell
                                  className={cn(
                                    "text-right font-medium",
                                    populatedAmt(computed.totalCompletedStored),
                                  )}
                                >
                                  {formatCurrency(computed.totalCompletedStored)}
                                </TableCell>
                                <TableCell className="text-right">
                                  {computed.pct.toFixed(1)}%
                                </TableCell>
                                <TableCell className="text-right">
                                  {formatCurrency(computed.balanceToFinish)}
                                </TableCell>
                                <TableCell className="text-right">
                                  {formatCurrency(computed.retainage)}
                                </TableCell>
                                {/* Source badge */}
                                <TableCell>
                                  {isGcDirect ? (
                                    <Badge
                                      variant="secondary"
                                      className="text-[10px] bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300"
                                    >
                                      GC DIRECT
                                    </Badge>
                                  ) : (
                                    <Badge variant="secondary" className="text-[10px]">
                                      SUB
                                    </Badge>
                                  )}
                                </TableCell>
                                {/* Drill-down / delete */}
                                <TableCell>
                                  <div className="flex items-center gap-1">
                                    {canDelete ? (
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-6 w-6 text-destructive hover:text-destructive"
                                        onClick={() => setDeleteConfirmId(item.id)}
                                      >
                                        <Trash2 className="h-3 w-3" />
                                      </Button>
                                    ) : null}
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-6 w-6"
                                      onClick={() => {
                                        if (isGcDirect) {
                                          setGcDrillDown({
                                            lineItemId: item.id,
                                            lineDescription: item.description ?? "",
                                          });
                                        } else {
                                          setSubDrillDown({
                                            costCodeId: item.costCodeId ?? null,
                                            description: item.description ?? "",
                                            thisPeriodTotal: computed.completedThisPeriod,
                                          });
                                        }
                                      }}
                                    >
                                      <ChevronRight className="h-3 w-3" />
                                    </Button>
                                  </div>
                                </TableCell>
                              </TableRow>
                            );
                          })}

                          {/* Division subtotal */}
                          <TableRow
                            key={`div-sub-${group.division}`}
                            className="bg-blue-50/50 dark:bg-blue-950/20 hover:bg-blue-50/50 dark:hover:bg-blue-950/20 font-medium"
                          >
                            <TableCell className="text-muted-foreground text-[10px]" />
                            <TableCell className="text-[10px] font-semibold text-muted-foreground">
                              {group.label} Subtotal
                            </TableCell>
                            <TableCell className="text-right">
                              {formatCurrency(divTotals.scheduledValue)}
                            </TableCell>
                            <TableCell className="text-right text-muted-foreground">—</TableCell>
                            <TableCell className="text-right text-muted-foreground">—</TableCell>
                            <TableCell className="text-right">
                              {formatCurrency(divTotals.revisedScheduledValue)}
                            </TableCell>
                            <TableCell className="text-right">
                              {formatCurrency(divTotals.prevApplications)}
                            </TableCell>
                            <TableCell className="text-right">
                              {formatCurrency(divTotals.completedThisPeriod)}
                            </TableCell>
                            <TableCell className="text-right">
                              {formatCurrency(divTotals.storedMaterials)}
                            </TableCell>
                            <TableCell className="text-right">
                              {formatCurrency(divTotals.totalCompletedStored)}
                            </TableCell>
                            <TableCell className="text-right">
                              {divTotals.revisedScheduledValue > 0
                                ? (
                                    (divTotals.totalCompletedStored /
                                      divTotals.revisedScheduledValue) *
                                    100
                                  ).toFixed(1)
                                : "0.0"}
                              %
                            </TableCell>
                            <TableCell className="text-right">
                              {formatCurrency(
                                divTotals.revisedScheduledValue - divTotals.totalCompletedStored,
                              )}
                            </TableCell>
                            <TableCell className="text-right">
                              {formatCurrency(divTotals.retainage)}
                            </TableCell>
                            <TableCell />
                            <TableCell />
                          </TableRow>

                          {/* Add GC Direct button in Division 50 */}
                          {group.division === "50" && isDraft && isPmOrAdmin ? (
                            <TableRow key="add-gc-direct" className="hover:bg-transparent">
                              <TableCell colSpan={COL_COUNT} className="py-2">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-blue-600 hover:text-blue-700"
                                  onClick={() => setAddGcDirectOpen(true)}
                                >
                                  <Plus className="mr-1 h-3 w-3" />
                                  Add GC Direct Line
                                </Button>
                              </TableCell>
                            </TableRow>
                          ) : null}
                        </Fragment>
                      );
                    })}

                    {/* If no Division 50 exists yet, show the Add button as a standalone row */}
                    {!divisionGroups.some((g) => g.division === "50") && isDraft && isPmOrAdmin ? (
                      <TableRow className="hover:bg-transparent">
                        <TableCell colSpan={COL_COUNT} className="py-3">
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-blue-600 hover:text-blue-700"
                            onClick={() => setAddGcDirectOpen(true)}
                          >
                            <Plus className="mr-1 h-3 w-3" />
                            Add GC Direct Line
                          </Button>
                        </TableCell>
                      </TableRow>
                    ) : null}

                    {/* Grand Total */}
                    <TableRow className="bg-muted/80 hover:bg-muted/80 font-bold border-t-2">
                      <TableCell />
                      <TableCell className="font-bold text-xs">Grand Total</TableCell>
                      <TableCell className="text-right">
                        {formatCurrency(grandTotals.scheduledValue)}
                      </TableCell>
                      <TableCell className="text-right text-muted-foreground">—</TableCell>
                      <TableCell className="text-right text-muted-foreground">—</TableCell>
                      <TableCell className="text-right">
                        {formatCurrency(grandTotals.revisedScheduledValue)}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatCurrency(grandTotals.prevApplications)}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatCurrency(grandTotals.completedThisPeriod)}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatCurrency(grandTotals.storedMaterials)}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatCurrency(grandTotals.totalCompletedStored)}
                      </TableCell>
                      <TableCell className="text-right">
                        {grandTotals.revisedScheduledValue > 0
                          ? (
                              (grandTotals.totalCompletedStored /
                                grandTotals.revisedScheduledValue) *
                              100
                            ).toFixed(1)
                          : "0.0"}
                        %
                      </TableCell>
                      <TableCell className="text-right">
                        {formatCurrency(
                          grandTotals.revisedScheduledValue - grandTotals.totalCompletedStored,
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatCurrency(grandTotals.retainage)}
                      </TableCell>
                      <TableCell />
                      <TableCell />
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
            )}
          </Card>
        </TabsContent>
      </Tabs>

      {/* Distribution */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Distribution</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="mb-3 text-sm text-muted-foreground">
            Who receives a copy of this pay application — rendered as checkmarks on the printed
            G702.
          </p>
          <div className="flex flex-wrap gap-6">
            {(
              [
                { key: "distribOwner", label: "Owner" },
                { key: "distribDesigner", label: "Designer (Architect)" },
                { key: "distribContractor", label: "Contractor" },
              ] as const
            ).map((flag) => (
              <label key={flag.key} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  className="h-4 w-4"
                  checked={payApp[flag.key]}
                  disabled={!isDraft || updateOwnerPayAppMutation.isPending}
                  onChange={(e) =>
                    updateOwnerPayAppMutation.mutate({ [flag.key]: e.target.checked })
                  }
                />
                {flag.label}
              </label>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Attachments */}
      <AttachmentSection payAppType="owner" payAppId={payAppId} readOnly={!isDraft} />

      {/* ── Dialogs ── */}

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Owner Pay Application</DialogTitle>
            <DialogDescription>
              Provide a reason for rejecting this pay application.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label>Reason</Label>
            <Textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Enter rejection reason…"
              rows={4}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleReject}
              disabled={rejectMutation.isPending || !rejectReason.trim()}
            >
              {rejectMutation.isPending ? "Rejecting…" : "Confirm Rejection"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Refresh Preview Dialog */}
      <Dialog open={refreshPreviewOpen} onOpenChange={setRefreshPreviewOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Refresh from Sub Pay Apps — Preview</DialogTitle>
            <DialogDescription>
              Review the proposed changes before applying. Work-completed amounts come from approved
              sub pay apps, and approved change orders are rolled up by cost code, both through the
              period end date.
            </DialogDescription>
          </DialogHeader>
          {previewLoading ? (
            <div className="space-y-2 py-4">
              <Skeleton className="h-6 w-full" />
              <Skeleton className="h-6 w-full" />
              <Skeleton className="h-6 w-full" />
            </div>
          ) : previewItems.length === 0 ? (
            <p className="py-4 text-sm text-muted-foreground">
              No changes detected — sub pay app amounts match the current line items.
            </p>
          ) : (
            (() => {
              const anyCoDelta = previewItems.some(
                (d) => d.proposed.approvedChangeOrders !== d.current.approvedChangeOrders,
              );
              return (
                <div className="overflow-x-auto">
                  <Table className="text-xs">
                    <TableHeader>
                      <TableRow>
                        <TableHead>Description</TableHead>
                        <TableHead className="text-right">Current This Period</TableHead>
                        <TableHead className="text-right">Proposed This Period</TableHead>
                        <TableHead className="text-right">Δ</TableHead>
                        {anyCoDelta ? (
                          <>
                            <TableHead className="text-right">Current App&apos;d COs</TableHead>
                            <TableHead className="text-right">Proposed App&apos;d COs</TableHead>
                            <TableHead className="text-right">Δ COs</TableHead>
                          </>
                        ) : null}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {previewItems.map((diff) => {
                        const delta =
                          diff.proposed.completedThisPeriod - diff.current.completedThisPeriod;
                        const coDelta =
                          diff.proposed.approvedChangeOrders - diff.current.approvedChangeOrders;
                        return (
                          <TableRow key={diff.lineId}>
                            <TableCell>{diff.description}</TableCell>
                            <TableCell className="text-right">
                              {formatCurrency(diff.current.completedThisPeriod)}
                            </TableCell>
                            <TableCell className="text-right font-medium">
                              {formatCurrency(diff.proposed.completedThisPeriod)}
                            </TableCell>
                            <TableCell
                              className={`text-right ${delta > 0 ? "text-green-600" : delta < 0 ? "text-red-600" : "text-muted-foreground"}`}
                            >
                              {delta > 0 ? "+" : ""}
                              {formatCurrency(delta)}
                            </TableCell>
                            {anyCoDelta ? (
                              <>
                                <TableCell className="text-right">
                                  {formatCurrency(diff.current.approvedChangeOrders)}
                                </TableCell>
                                <TableCell className="text-right font-medium">
                                  {formatCurrency(diff.proposed.approvedChangeOrders)}
                                </TableCell>
                                <TableCell
                                  className={`text-right ${coDelta > 0 ? "text-green-600" : coDelta < 0 ? "text-red-600" : "text-muted-foreground"}`}
                                >
                                  {coDelta > 0 ? "+" : ""}
                                  {formatCurrency(coDelta)}
                                </TableCell>
                              </>
                            ) : null}
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              );
            })()
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setRefreshPreviewOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleRefreshApply}
              disabled={refreshFromSubsMutation.isPending || previewLoading}
            >
              <RefreshCw className="mr-2 h-4 w-4" />
              {refreshFromSubsMutation.isPending ? "Applying…" : "Apply Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* GR Refresh Preview Dialog */}
      <Dialog open={grRefreshPreviewOpen} onOpenChange={setGrRefreshPreviewOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Refresh GR Line Items</DialogTitle>
            <DialogDescription>
              Updates G703 line items for General Requirements SOV lines based on posted GR
              invoices.
            </DialogDescription>
          </DialogHeader>
          {grPreviewLoading ? (
            <Skeleton className="h-32 w-full" />
          ) : (grRefreshPreviewData?.preview ?? []).length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No GR line items have changes to apply for this billing period.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Item #</TableHead>
                  <TableHead className="text-right">GR Total</TableHead>
                  <TableHead className="text-right">Prev. Period</TableHead>
                  <TableHead className="text-right">Proposed</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(grRefreshPreviewData?.preview ?? []).map((diff: GrRefreshDiffItem) => (
                  <TableRow key={diff.lineId}>
                    <TableCell>{diff.itemNo ?? "—"}</TableCell>
                    <TableCell className="text-right">{formatCurrency(diff.grTotal)}</TableCell>
                    <TableCell className="text-right">{formatCurrency(diff.prevTotal)}</TableCell>
                    <TableCell className="text-right font-semibold">
                      {formatCurrency(diff.proposed)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setGrRefreshPreviewOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() =>
                refreshFromGRsMutation.mutate(undefined, {
                  onSuccess: () => setGrRefreshPreviewOpen(false),
                })
              }
              disabled={refreshFromGRsMutation.isPending || grPreviewLoading}
            >
              <RefreshCw className="mr-2 h-4 w-4" />
              {refreshFromGRsMutation.isPending ? "Applying…" : "Apply GR Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add GC Direct Line Dialog */}
      <Dialog open={addGcDirectOpen} onOpenChange={setAddGcDirectOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add GC Direct Cost Line</DialogTitle>
            <DialogDescription>
              Add a GC-controlled cost line to the G703 continuation sheet.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Cost Code</Label>
              <Select
                value={addGcForm.costCodeId ?? ""}
                onValueChange={(val) =>
                  setAddGcForm((prev) => ({ ...prev, costCodeId: val === "none" ? "" : val }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select cost code (optional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No cost code</SelectItem>
                  {(costCodes ?? []).map((cc) => (
                    <SelectItem key={cc.id} value={cc.id}>
                      {cc.code} — {cc.description}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>
                Description <span className="text-destructive">*</span>
              </Label>
              <Input
                value={addGcForm.description}
                onChange={(e) => setAddGcForm((prev) => ({ ...prev, description: e.target.value }))}
                placeholder="e.g. General Conditions"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>
                  Scheduled Value (C) <span className="text-destructive">*</span>
                </Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={addGcForm.scheduledValue}
                  onChange={(e) =>
                    setAddGcForm((prev) => ({ ...prev, scheduledValue: e.target.value }))
                  }
                  placeholder="0.00"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Adjustment (D)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={addGcForm.adjustmentToSchedule ?? ""}
                  onChange={(e) =>
                    setAddGcForm((prev) => ({ ...prev, adjustmentToSchedule: e.target.value }))
                  }
                  placeholder="0.00"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Approved COs (E)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={addGcForm.approvedChangeOrders ?? ""}
                  onChange={(e) =>
                    setAddGcForm((prev) => ({ ...prev, approvedChangeOrders: e.target.value }))
                  }
                  placeholder="0.00"
                />
              </div>
              <div className="space-y-1.5">
                <Label>This Period (H)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={addGcForm.completedThisPeriod ?? ""}
                  onChange={(e) =>
                    setAddGcForm((prev) => ({ ...prev, completedThisPeriod: e.target.value }))
                  }
                  placeholder="0.00"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Stored Materials (I)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={addGcForm.storedMaterials ?? ""}
                  onChange={(e) =>
                    setAddGcForm((prev) => ({ ...prev, storedMaterials: e.target.value }))
                  }
                  placeholder="0.00"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Retention % Override</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={addGcForm.retentionPercent ?? ""}
                  onChange={(e) =>
                    setAddGcForm((prev) => ({ ...prev, retentionPercent: e.target.value }))
                  }
                  placeholder={`${retPct} (default)`}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setAddGcDirectOpen(false);
                setAddGcForm(defaultAddForm);
              }}
            >
              Cancel
            </Button>
            <Button onClick={handleAddGcDirect} disabled={createGcDirectMutation.isPending}>
              {createGcDirectMutation.isPending ? "Adding…" : "Add Line Item"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirm Dialog */}
      <Dialog
        open={deleteConfirmId !== null}
        onOpenChange={(open) => {
          if (!open) setDeleteConfirmId(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete GC Direct Line</DialogTitle>
            <DialogDescription>
              This line has never been billed. Are you sure you want to delete it?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirmId(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={deleteGcDirectMutation.isPending}
              onClick={() => {
                if (deleteConfirmId) handleDeleteGcDirect(deleteConfirmId);
              }}
            >
              {deleteGcDirectMutation.isPending ? "Deleting…" : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Drill-down Sheets ── */}

      {subDrillDown ? (
        <SubSourcedDrillDownSheet
          payAppId={payAppId}
          costCodeId={subDrillDown.costCodeId}
          costCodeDesc={subDrillDown.description}
          thisPeriodTotal={subDrillDown.thisPeriodTotal}
          open={subDrillDown !== null}
          onOpenChange={(open) => {
            if (!open) setSubDrillDown(null);
          }}
        />
      ) : null}

      {gcDrillDown ? (
        <GcDirectDrillDownSheet
          payAppId={payAppId}
          lineItemId={gcDrillDown.lineItemId}
          lineDescription={gcDrillDown.lineDescription}
          open={gcDrillDown !== null}
          onOpenChange={(open) => {
            if (!open) setGcDrillDown(null);
          }}
        />
      ) : null}

      <ReauthDialog open={reauthOpen} onOpenChange={onReauthOpenChange} onVerified={onVerified} />
    </div>
  );
}
